import { memo } from "react"

import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { AgentRun } from "../types"

import { getEmptyStates } from "./admin-dashboard-empty.utils"

const CHART_HEIGHT = {
  MEDIUM: 260,
} as const

export const AgentRunsChart = memo(function AgentRunsChart({
  data,
}: {
  data: AgentRun[] | null | undefined
}) {
  const { t } = useTranslation()
  const EMPTY_STATES = getEmptyStates(t)

  if (!data || data.length === 0) {
    return (
      <div className="text-muted-foreground flex h-[300px] items-center justify-center text-sm">
        {EMPTY_STATES.agentRuns.title}
      </div>
    )
  }

  // Calculate interval for x-axis labels to avoid overcrowding
  // Match token chart behavior: if more than 30 points, show every few
  const xAxisInterval = data.length > 30 ? Math.floor(data.length / 8) : 0

  return (
    <ResponsiveContainer
      aria-label="Research agent runs chart showing execution trends"
      height={CHART_HEIGHT.MEDIUM}
      width="100%"
    >
      <LineChart data={data}>
        <CartesianGrid className="stroke-muted" strokeDasharray="3 3" />
        <XAxis
          angle={-45}
          className="text-muted-foreground"
          dataKey="label"
          height={70}
          interval={xAxisInterval}
          minTickGap={20}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />
        <YAxis className="text-muted-foreground" />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "0.5rem",
            color: "hsl(var(--card-foreground))",
          }}
        />
        <Legend />
        <Line dataKey="runs" stroke="hsl(var(--chart-2))" strokeWidth={2} type="monotone" />
      </LineChart>
    </ResponsiveContainer>
  )
})
